// Translation dictionary for Saudi Delta Energy. English canonical source;
// Arabic translations cover navigation, all page hero eyebrows/titles/subs,
// all section eyebrows + H2 titles, all CTAs, table headings, and form labels.
// Long descriptive body paragraphs and data tables intentionally remain in
// English to preserve technical accuracy of corporate copy.

export const translations = {
    en: {
        nav: {
            home: 'Home',
            about: 'About',
            history: 'History',
            leadership: 'Leadership',
            capabilities: 'Capabilities',
            strategy: 'Strategy',
            projects: 'Projects',
            global: 'Global Presence',
            partnerships: 'Partnerships',
            contact: 'Contact',
            language: 'EN',
            switch_to: 'العربية',
        },
        common: {
            learn_more: 'Learn More',
            read_more: 'Read More',
            view_project: 'View Project',
            see_all: 'See All',
            see_full_history: 'See Full History',
            get_in_touch: 'Get in Touch',
            download_profile: 'Download Company Profile',
            explore_projects: 'Explore Our Projects',
            send_enquiry: 'Send Enquiry',
            read_story: 'Read Our Story',
            view_contact: 'View Contact Information',
            active: 'Active',
            historical: 'Historical',
            sending: 'Sending…',
        },
        hero: {
            eyebrow: 'Saudi Delta Energy Company · SDEC',
            h1: 'An Independent Integrated Oil & Gas Platform',
            sub: 'Part of Delta International Group Ltd. — Rooted in Saudi Arabia. Operating across four continents.',
        },
        metrics: {
            founded: 'Founded',
            reserves: 'Caspian reserves (ACG)',
            return: 'Return on Centurion Energy',
            continents: 'Continents · Active projects',
            pipeline: 'Pipeline of investments',
        },
        home: {
            who_eyebrow: 'About Delta International Energy',
            who_h2: 'Built on Four Decades of Landmark Energy Transactions',
            who_body: "Delta International Energy is the Oil & Gas division of Delta International Group Ltd., a privately held Saudi investment group founded in 1978 and headquartered in Jeddah, Kingdom of Saudi Arabia. From the Caspian Sea to the Albertine Graben, from the Nile Delta to the Afghan gas basins — Delta has consistently positioned itself as an early mover in the world's most consequential frontier energy markets.",
            who_quote: "\"Our ambition is to become a major independent integrated oil and gas organization — built on three decades of landmark transactions, strategic partnerships, and a relentless commitment to unlocking the world's most promising energy frontiers.\"",
            who_attrib: '— Badr Bin Mohammed Al Aiban, Chairman',
            pillars_eyebrow: 'Our Strategy',
            pillars_h2: 'Three Pillars. One Vision.',
            projects_eyebrow: 'Current Portfolio',
            projects_h2: 'Six High-Impact Investments Across Three Continents',
            timeline_eyebrow: 'Proven Track Record',
            timeline_h2: 'From the Contract of the Century to Today',
            partners_eyebrow: 'Strategic Partnerships',
            partners_h2: "Working Alongside the World's Leading Energy Companies",
            cta_h2: 'Explore a Partnership with Delta International Energy',
            cta_sub: 'We welcome inquiries from sovereign governments, national oil companies, institutional investors, and strategic co-investors.',
        },
        pillars: {
            p1_title: 'Frontier Exploration',
            p1_desc: 'We identify high-impact basins before the mainstream investment community arrives — securing positions of strategic importance before large-scale capital deployment drives up entry costs.',
            p2_title: 'Integrated Gas Development',
            p2_desc: 'We build entire energy economies from a single gas resource — from wellhead through pipeline transmission to power generation, LNG export, and industrial supply.',
            p3_title: 'Sovereign Partnership Model',
            p3_desc: 'We invest with countries, not just in them — aligning capital, expertise, and long-term commitment with national development objectives. No upfront government capital required.',
        },
        about: {
            hero_eyebrow: 'About',
            hero_title: 'An Independent Energy Platform Rooted in Saudi Arabia',
            hero_sub: 'Delta International Energy combines Saudi institutional credibility with an international operating track record spanning the Caspian Sea, North Africa, Central Asia, Latin America, and Sub-Saharan Africa.',
            company_eyebrow: 'The Company',
            company_h2: 'One Integrated Energy Platform',
            identity_eyebrow: 'Corporate Identity at a Glance',
            mission_label: 'Mission',
            vision_label: 'Vision',
            values_eyebrow: 'What We Stand For',
            values_h2: 'Core Values',
            values_sub: 'Five enduring principles that guide every Delta investment, partnership, and operating decision.',
            adv_eyebrow: 'What Sets Us Apart',
            adv_h2: 'Five Competitive Advantages',
            adv_sub: "Delta's competitive position is built on a rare combination of sovereign credibility, frontier expertise, and disciplined execution.",
            group_eyebrow: 'The Group',
            group_h2: 'Part of a Diversified Saudi Investment Group',
            group_sub: 'Delta International Energy operates within the broader Delta International Group Ltd. ecosystem — a diversified Saudi investment group with activities spanning energy, real estate, and industrial sectors across multiple continents.',
            parent_label: 'Parent',
            company_body_1: "Delta International Energy is the dedicated Oil & Gas division of Delta International Group Ltd. Operating under the banner of Saudi Delta Energy Company (SDEC), the company consolidates upstream exploration and production, midstream pipeline and infrastructure, and downstream gas monetization activities under a single, integrated platform.",
            company_body_2: "Established to pursue the Group's founding ambition — to play a strategic and enduring role in the development of the global oil and gas industry — Delta combines Saudi institutional credibility with an international operating track record across some of the world's most consequential energy basins.",
            mission_body: "To become a major independent integrated oil and gas organization — identifying, developing, and monetizing the world's most promising energy resources through disciplined investment, innovative financing, and sovereign partnerships that create lasting value for all stakeholders.",
            vision_body: "To be recognized as a leading privately held energy platform from the Arab world — one that combines the financial strength and political intelligence of the Gulf with the technical rigor and operational discipline of the world's best international oil companies.",
            id_parent_group: 'Parent Group',
            id_energy_division: 'Energy Division',
            id_operating: 'Operating Entity',
            id_ownership: 'Ownership',
            id_ownership_val: 'Privately Held — Saudi',
            id_founded: 'Founded',
            id_hq: 'Headquarters',
            id_hq_val: 'Jeddah, Kingdom of Saudi Arabia',
            id_website: 'Website',
            id_email: 'Email',
            group_delta_energy: 'Delta Energy',
            group_delta_energy_desc: 'Oil & Gas — Our Business',
            group_delta_real: 'Delta Real Estate',
            group_delta_real_desc: 'Property & Development',
            group_delta_ind: 'Delta Industrial',
            group_delta_ind_desc: 'Diversified Industrial',
        },
        history: {
            hero_eyebrow: 'History & Milestones',
            hero_title: 'Built on Four Decades of Landmark Energy Transactions',
            hero_sub: "From a private Saudi investment group founded in Jeddah to a participant in the Contract of the Century — Delta International Energy's history is a record of bold, early-mover decisions in the world's most significant emerging energy basins.",
            summary_eyebrow: 'Milestone Summary',
            summary_h2: 'Four Decades at a Glance',
            col_year: 'Year',
            col_milestone: 'Milestone',
        },
        leadership: {
            hero_eyebrow: 'Leadership',
            hero_title: 'Guided by Vision. Driven by Experience.',
            hero_sub: "Delta International Energy is led by its founder, whose four decades at the helm have shaped a culture defined by bold conviction, disciplined execution, and an unwavering commitment to building long-term value in the world's most consequential energy markets.",
            profile_eyebrow: 'Chairman Profile',
            chairman_title: 'Chairman & Chief Executive Officer · Delta International Group Ltd.',
            founding_vision: 'Founding Vision',
            track_eyebrow: 'Track Record',
            track_h2: 'Career-Defining Transactions',
            principles_eyebrow: 'Leadership Philosophy',
            principles_h2: "Five Principles That Define Delta's Culture",
            chairman_name: 'Badr Bin Mohammed Al Aiban',
            chairman_role: 'Founder · Chairman · CEO',
            stat_education: 'Education',
            stat_education_val: 'USC',
            stat_experience: 'Experience',
            stat_experience_val: '40+ years',
            stat_regions: 'Regions',
            stat_regions_val: '5 continents',
            stat_deals: 'Landmark Deals',
            stat_deals_val: '4+',
        },
        capabilities: {
            hero_eyebrow: 'Capabilities',
            hero_title: 'An Integrated Energy Platform Built on Four Decades of Experience',
            hero_sub: "Delta International Energy's competitive advantage rests on a combination of technical expertise, financial engineering capability, and geopolitical intelligence — assembled across the world's most complex and consequential energy markets.",
            chain_eyebrow: 'Value Chain',
            chain_h2: 'Active Across All Three Segments',
            chain_sub: 'From wellhead to power plant, pipeline to LNG terminal — Delta operates and invests across the complete oil & gas value chain.',
            summary_eyebrow: 'Capability Summary',
            summary_h2: 'Where We Deploy',
            track_label: 'Track Record',
            col_cap: 'Capability',
            col_region: 'Regions of Application',
        },
        strategy: {
            hero_eyebrow: 'Strategic Vision',
            hero_title: 'Building the Next Generation Independent Energy Company',
            hero_sub: "Delta International Energy's strategic vision is to become a major independent integrated oil and gas organization — combining upstream exploration capability, midstream infrastructure reach, and downstream monetization sophistication while preserving the agility of a privately held, founder-led enterprise.",
            criteria_eyebrow: 'Investment Criteria',
            criteria_h2: 'Six Filters for Every Opportunity',
            gas_eyebrow: 'Integrated Gas Value Chain',
            gas_h2: 'Five Revenue Streams from a Single Resource',
            philosophy_eyebrow: 'Investment Philosophy',
            philosophy_h2: 'Five Principles Governing Every Decision',
            outlook_eyebrow: 'Strategic Outlook',
            outlook_h2: 'Three Converging Forces Reshaping Global Energy',
            col_criterion: 'Criterion',
            col_req: 'Requirement',
            gas_source: 'Gas Resource',
        },
        projects: {
            hero_eyebrow: 'Projects',
            hero_title: 'Six High-Impact Investments Across Three Continents',
            hero_sub: "Each project reflects Delta's frontier exploration philosophy, integrated value chain approach, and sovereign partnership model — one of the most geographically and technically diversified portfolios held by any privately held Arab energy investor.",
            col_project: 'Project',
            col_geo: 'Geography',
            col_comm: 'Commodity',
            col_stage: 'Stage',
            col_role: 'Delta Role',
        },
        global: {
            hero_eyebrow: 'Global Presence',
            hero_title: 'Rooted in Saudi Arabia. Active Across the World.',
            hero_sub: 'Delta International Energy operates from its founding home in the Kingdom of Saudi Arabia — extending its reach across three continents through an active portfolio of oil and gas investment programs.',
            hq_label: 'Headquarters',
            hq_h2: 'Jeddah, Kingdom of Saudi Arabia',
            map_eyebrow: 'Active Geographies',
            map_h2: 'A Portfolio That Spans Three Continents',
            active_label: 'Active Operations',
            historical_label: 'Historical Operations',
            footprint_eyebrow: 'Geographic Footprint',
            footprint_h2: 'Summary',
            historical_geo: 'Historical Geographies',
        },
        partnerships: {
            hero_eyebrow: 'Partnerships',
            hero_title: 'Built on Three Decades of Trust, Performance, and Shared Ambition',
            hero_sub: "Delta International Energy's partnership network spans international oil majors, national oil companies, independent operators, sovereign governments, and world-class engineering and construction firms.",
            network_eyebrow: 'Partner Network',
            network_h2: "Working Alongside the World's Leading Energy Companies",
            historical_partners: 'Historical Partners',
            active_partners: 'Active Pipeline Partners',
            details_eyebrow: 'Partnership Details',
            details_h2: 'Relationships That Define an Industry',
            tab_historical: 'Historical',
            tab_active: 'Active EPC Pipeline',
            summary_eyebrow: 'Partnership Network Summary',
            col_partner: 'Partner',
            col_type: 'Type',
            col_period: 'Period',
            col_context: 'Context',
        },
        contact_page: {
            hero_eyebrow: 'Contact',
            hero_title: 'Get in Touch with Delta International Energy',
            hero_sub: 'We welcome inquiries from sovereign governments, national oil companies, institutional investors, international operators, and strategic partners who share our commitment to unlocking the world\'s most promising energy opportunities.',
            hq_eyebrow: 'Headquarters',
            hq_h2: 'Delta International Group Ltd.',
            hq_loc: 'Jeddah, Kingdom of Saudi Arabia',
            address_label: 'Address',
            phone_label: 'Telephone',
            email_label: 'Email',
            website_label: 'Website',
            inquiries_label: 'Project Inquiries',
            form_eyebrow: 'Send an Enquiry',
            form_h2: "We'd like to hear from you.",
            closing_eyebrow: 'Closing Statement',
            closing_quote: "\"Delta International Energy was founded on the belief that the Arab world's energy expertise, capital, and sovereign relationships could be combined with international technical and financial standards to build a truly world-class independent energy company. Four decades later — from the Caspian Sea to the Albertine Graben, from the Nile Delta to the Afghan gas basins — that belief has been vindicated. The next chapter is our most ambitious yet.\"",
        },
        footer: {
            tagline: 'Investments · Technology · Solutions',
            rights: 'All rights reserved.',
            quick_links: 'Explore',
            contact_us: 'Contact',
            address: '2827 Hassan Naib Al Haram Street, 8989 Al Andalus District, Jeddah 23326, Kingdom of Saudi Arabia',
            col_explore: 'Explore',
            col_portfolio: 'Portfolio',
            jeddah_sa: 'Jeddah, Saudi Arabia',
            about_blurb: 'An independent integrated oil & gas platform. Part of Delta International Group Ltd. — Rooted in Saudi Arabia. Operating across four continents.',
            headquarters: 'Headquarters',
        },
        contact: {
            full_name: 'Full Name',
            company: 'Company',
            country: 'Country',
            email: 'Email',
            enquiry_type: 'Enquiry Type',
            message: 'Message',
            placeholder_enquiry: 'Select enquiry type',
            success_title: 'Enquiry sent',
            success_desc: 'Thank you. The Delta International Energy team will respond to you shortly.',
            error_title: 'Submission failed',
            error_desc: 'Please review your details and try again.',
            types: {
                investment: 'Investment Partnership',
                project: 'Project Enquiry',
                media: 'Media',
                other: 'Other',
            },
        },
    },
    ar: {
        nav: {
            home: 'الرئيسية',
            about: 'نبذة عنا',
            history: 'التاريخ',
            leadership: 'القيادة',
            capabilities: 'القدرات',
            strategy: 'الاستراتيجية',
            projects: 'المشاريع',
            global: 'الحضور العالمي',
            partnerships: 'الشراكات',
            contact: 'تواصل معنا',
            language: 'AR',
            switch_to: 'English',
        },
        common: {
            learn_more: 'اكتشف المزيد',
            read_more: 'اقرأ المزيد',
            view_project: 'عرض المشروع',
            see_all: 'عرض الكل',
            see_full_history: 'استعرض التاريخ الكامل',
            get_in_touch: 'تواصل معنا',
            download_profile: 'تحميل ملف الشركة',
            explore_projects: 'استكشف مشاريعنا',
            send_enquiry: 'إرسال الاستفسار',
            read_story: 'اقرأ قصتنا',
            view_contact: 'معلومات التواصل',
            active: 'نشط',
            historical: 'تاريخي',
            sending: 'جاري الإرسال…',
        },
        hero: {
            eyebrow: 'شركة دلتا السعودية للطاقة',
            h1: 'منصة مستقلة ومتكاملة للنفط والغاز',
            sub: 'جزء من مجموعة دلتا الدولية المحدودة — جذورنا في المملكة العربية السعودية، ونعمل عبر أربع قارات.',
        },
        metrics: {
            founded: 'تأسست',
            reserves: 'احتياطيات بحر قزوين',
            return: 'عائد سنتوريون للطاقة',
            continents: 'قارات · مشاريع نشطة',
            pipeline: 'محفظة الاستثمارات',
        },
        home: {
            who_eyebrow: 'نبذة عن دلتا الدولية للطاقة',
            who_h2: 'مبنية على أربعة عقود من الصفقات الكبرى',
            who_body: 'دلتا الدولية للطاقة هي قطاع النفط والغاز التابع لمجموعة دلتا الدولية المحدودة، وهي مجموعة استثمارية سعودية مملوكة للقطاع الخاص تأسست عام 1978 ومقرها جدة بالمملكة العربية السعودية. من بحر قزوين إلى وادي ألبرتاين، ومن دلتا النيل إلى الأحواض الغازية الأفغانية — دائمًا ما كانت دلتا من أوائل الداخلين إلى أهم أسواق الطاقة الناشئة في العالم.',
            who_quote: '"طموحنا أن نصبح مؤسسة كبرى مستقلة ومتكاملة للنفط والغاز — مبنية على ثلاثة عقود من الصفقات الكبرى والشراكات الاستراتيجية والالتزام الدائم بفتح آفاق الطاقة الواعدة في العالم."',
            who_attrib: '— بدر بن محمد العيبان، رئيس مجلس الإدارة',
            pillars_eyebrow: 'استراتيجيتنا',
            pillars_h2: 'ثلاث ركائز. رؤية واحدة.',
            projects_eyebrow: 'المحفظة الحالية',
            projects_h2: 'ستة استثمارات استراتيجية عبر ثلاث قارات',
            timeline_eyebrow: 'سجل حافل',
            timeline_h2: 'من عقد القرن إلى اليوم',
            partners_eyebrow: 'الشراكات الاستراتيجية',
            partners_h2: 'بجانب أبرز شركات الطاقة في العالم',
            cta_h2: 'استكشف شراكة مع دلتا الدولية للطاقة',
            cta_sub: 'نرحب بالاستفسارات من الحكومات السيادية وشركات النفط الوطنية والمستثمرين المؤسسيين والشركاء الاستراتيجيين.',
        },
        pillars: {
            p1_title: 'الاستكشاف في الحدود الجديدة',
            p1_desc: 'نحدد الأحواض ذات التأثير العالي قبل وصول مجتمع الاستثمار الرئيسي — مما يضمن لنا مواقع ذات أهمية استراتيجية قبل أن يرفع نشر رؤوس الأموال الكبيرة تكلفة الدخول.',
            p2_title: 'تطوير الغاز المتكامل',
            p2_desc: 'نبني اقتصاديات طاقة متكاملة من مورد غازي واحد — من رأس البئر عبر نقل خطوط الأنابيب إلى توليد الطاقة وتصدير الغاز الطبيعي المسال والإمداد الصناعي.',
            p3_title: 'نموذج الشراكة السيادية',
            p3_desc: 'نستثمر مع الدول، لا فقط فيها — بمواءمة رأس المال والخبرة والالتزام طويل الأمد مع أهداف التنمية الوطنية. دون الحاجة إلى أي رأس مال حكومي مسبق.',
        },
        about: {
            hero_eyebrow: 'نبذة عنا',
            hero_title: 'منصة طاقة مستقلة جذورها في المملكة العربية السعودية',
            hero_sub: 'دلتا الدولية للطاقة تجمع بين المصداقية المؤسسية السعودية وسجل تشغيلي دولي يمتد عبر بحر قزوين وشمال إفريقيا وآسيا الوسطى وأمريكا اللاتينية وأفريقيا جنوب الصحراء.',
            company_eyebrow: 'الشركة',
            company_h2: 'منصة طاقة متكاملة واحدة',
            identity_eyebrow: 'الهوية المؤسسية بإيجاز',
            mission_label: 'الرسالة',
            vision_label: 'الرؤية',
            values_eyebrow: 'ما نمثله',
            values_h2: 'القيم الجوهرية',
            values_sub: 'خمسة مبادئ راسخة توجه كل استثمار وشراكة وقرار تشغيلي في دلتا.',
            adv_eyebrow: 'ما يميزنا',
            adv_h2: 'خمسة عوامل تنافسية',
            adv_sub: 'يستند موقف دلتا التنافسي إلى مزيج نادر من المصداقية السيادية، والخبرة في الأسواق الناشئة، والتنفيذ المنضبط.',
            group_eyebrow: 'المجموعة',
            group_h2: 'جزء من مجموعة استثمارية سعودية متنوعة',
            group_sub: 'تعمل دلتا الدولية للطاقة ضمن منظومة مجموعة دلتا الدولية المحدودة — مجموعة استثمارية سعودية متنوعة لها أنشطة تشمل الطاقة والعقارات والقطاعات الصناعية عبر قارات متعددة.',
            parent_label: 'الشركة الأم',
            company_body_1: 'دلتا الدولية للطاقة هي قطاع النفط والغاز المتخصص في مجموعة دلتا الدولية المحدودة. وتعمل تحت اسم شركة دلتا السعودية للطاقة (SDEC)، حيث تدمج أنشطة الاستكشاف والإنتاج في القطاع العلوي، وأنابيب الغاز والبنية التحتية في القطاع الأوسط، وتحقيق قيمة الغاز في القطاع السفلي، في منصة واحدة متكاملة.',
            company_body_2: 'تأسست لتحقيق طموح المجموعة المؤسس — في أداء دور استراتيجي ومستدام في تطوير صناعة النفط والغاز العالمية — وتجمع دلتا بين المصداقية المؤسسية السعودية وسجل تشغيلي دولي عبر بعض من أهم أحواض الطاقة في العالم.',
            mission_body: 'أن نصبح مؤسسة كبرى مستقلة ومتكاملة للنفط والغاز — تعمل على تحديد وتطوير وتحقيق القيمة من أكثر موارد الطاقة الواعدة في العالم من خلال الاستثمار المنضبط والتمويل المبتكر والشراكات السيادية التي تخلق قيمة دائمة لجميع أصحاب المصلحة.',
            vision_body: 'أن نُعرف كمنصة طاقة رائدة مملوكة للقطاع الخاص من العالم العربي — تجمع بين القوة المالية والذكاء السياسي للخليج وبين الصرامة الفنية والانضباط التشغيلي لأفضل شركات النفط الدولية في العالم.',
            id_parent_group: 'المجموعة الأم',
            id_energy_division: 'قطاع الطاقة',
            id_operating: 'الكيان التشغيلي',
            id_ownership: 'الملكية',
            id_ownership_val: 'مملوكة للقطاع الخاص — سعودية',
            id_founded: 'تأسست',
            id_hq: 'المقر الرئيسي',
            id_hq_val: 'جدة، المملكة العربية السعودية',
            id_website: 'الموقع الإلكتروني',
            id_email: 'البريد الإلكتروني',
            group_delta_energy: 'دلتا للطاقة',
            group_delta_energy_desc: 'النفط والغاز — أعمالنا',
            group_delta_real: 'دلتا العقارية',
            group_delta_real_desc: 'العقارات والتطوير',
            group_delta_ind: 'دلتا الصناعية',
            group_delta_ind_desc: 'صناعات متنوعة',
        },
        history: {
            hero_eyebrow: 'التاريخ والإنجازات',
            hero_title: 'مبنية على أربعة عقود من الصفقات الكبرى في قطاع الطاقة',
            hero_sub: 'من مجموعة استثمارية سعودية خاصة تأسست في جدة إلى مشارك في عقد القرن — تاريخ دلتا الدولية للطاقة هو سجل من القرارات الجريئة والمبكرة في أهم أحواض الطاقة الناشئة في العالم.',
            summary_eyebrow: 'ملخص الإنجازات',
            summary_h2: 'أربعة عقود في لمحة',
            col_year: 'السنة',
            col_milestone: 'الإنجاز',
        },
        leadership: {
            hero_eyebrow: 'القيادة',
            hero_title: 'مدفوعون بالرؤية. مدعومون بالخبرة.',
            hero_sub: 'تقود دلتا الدولية للطاقة مؤسسها الذي شكلت أربعة عقود من قيادته ثقافة تتسم بالقناعة الجريئة والتنفيذ المنضبط والالتزام الراسخ ببناء قيمة طويلة الأمد في أهم أسواق الطاقة في العالم.',
            profile_eyebrow: 'ملف رئيس مجلس الإدارة',
            chairman_title: 'رئيس مجلس الإدارة والرئيس التنفيذي · مجموعة دلتا الدولية المحدودة',
            founding_vision: 'الرؤية التأسيسية',
            track_eyebrow: 'السجل',
            track_h2: 'صفقات شكّلت مسيرة',
            principles_eyebrow: 'فلسفة القيادة',
            principles_h2: 'خمسة مبادئ تُعرّف ثقافة دلتا',
            chairman_name: 'بدر بن محمد العيبان',
            chairman_role: 'المؤسس · رئيس مجلس الإدارة · الرئيس التنفيذي',
            stat_education: 'التعليم',
            stat_education_val: 'جامعة جنوب كاليفورنيا',
            stat_experience: 'الخبرة',
            stat_experience_val: 'أكثر من 40 عامًا',
            stat_regions: 'المناطق',
            stat_regions_val: '5 قارات',
            stat_deals: 'الصفقات الكبرى',
            stat_deals_val: '4+',
        },
        capabilities: {
            hero_eyebrow: 'القدرات',
            hero_title: 'منصة طاقة متكاملة مبنية على أربعة عقود من الخبرة',
            hero_sub: 'تستند الميزة التنافسية لدلتا الدولية للطاقة إلى مزيج من الخبرة الفنية والقدرة على الهندسة المالية والذكاء الجيوسياسي — تم تكوينها عبر أكثر أسواق الطاقة تعقيدًا وأهمية في العالم.',
            chain_eyebrow: 'سلسلة القيمة',
            chain_h2: 'نشاط عبر القطاعات الثلاثة جميعًا',
            chain_sub: 'من رأس البئر إلى محطة الطاقة، ومن خط الأنابيب إلى محطة الغاز الطبيعي المسال — تعمل دلتا وتستثمر عبر سلسلة قيمة النفط والغاز الكاملة.',
            summary_eyebrow: 'ملخص القدرات',
            summary_h2: 'مناطق التطبيق',
            track_label: 'السجل',
            col_cap: 'القدرة',
            col_region: 'مناطق التطبيق',
        },
        strategy: {
            hero_eyebrow: 'الرؤية الاستراتيجية',
            hero_title: 'بناء الجيل القادم من شركات الطاقة المستقلة',
            hero_sub: 'تتمثل الرؤية الاستراتيجية لدلتا الدولية للطاقة في أن تصبح مؤسسة كبرى مستقلة ومتكاملة للنفط والغاز — تجمع بين قدرات الاستكشاف العلوي وامتداد البنية التحتية في القطاع الأوسط وتطور تحقيق القيمة في القطاع السفلي، مع الحفاظ على مرونة شركة خاصة يقودها مؤسسها.',
            criteria_eyebrow: 'معايير الاستثمار',
            criteria_h2: 'ستة معايير لكل فرصة',
            gas_eyebrow: 'سلسلة القيمة المتكاملة للغاز',
            gas_h2: 'خمسة مصادر إيرادات من مورد واحد',
            philosophy_eyebrow: 'فلسفة الاستثمار',
            philosophy_h2: 'خمسة مبادئ تحكم كل قرار',
            outlook_eyebrow: 'النظرة الاستراتيجية',
            outlook_h2: 'ثلاث قوى متلاقية تعيد تشكيل قطاع الطاقة العالمي',
            col_criterion: 'المعيار',
            col_req: 'المتطلب',
            gas_source: 'مورد الغاز',
        },
        projects: {
            hero_eyebrow: 'المشاريع',
            hero_title: 'ستة استثمارات استراتيجية عبر ثلاث قارات',
            hero_sub: 'يعكس كل مشروع فلسفة دلتا في استكشاف الأسواق الناشئة، ونهجها المتكامل لسلسلة القيمة، ونموذج الشراكة السيادية — لتُشكّل واحدة من أكثر المحافظ تنوعًا جغرافيًا وتقنيًا التي يحتفظ بها أي مستثمر عربي خاص في قطاع الطاقة.',
            col_project: 'المشروع',
            col_geo: 'الجغرافيا',
            col_comm: 'السلعة',
            col_stage: 'المرحلة',
            col_role: 'دور دلتا',
        },
        global: {
            hero_eyebrow: 'الحضور العالمي',
            hero_title: 'جذورنا في المملكة العربية السعودية. حضورنا حول العالم.',
            hero_sub: 'تعمل دلتا الدولية للطاقة من موطنها التأسيسي في المملكة العربية السعودية — وتمتد عبر ثلاث قارات من خلال محفظة نشطة من برامج الاستثمار في النفط والغاز.',
            hq_label: 'المقر الرئيسي',
            hq_h2: 'جدة، المملكة العربية السعودية',
            map_eyebrow: 'المناطق النشطة',
            map_h2: 'محفظة تمتد عبر ثلاث قارات',
            active_label: 'العمليات النشطة',
            historical_label: 'العمليات التاريخية',
            footprint_eyebrow: 'البصمة الجغرافية',
            footprint_h2: 'ملخص',
            historical_geo: 'الجغرافيات التاريخية',
        },
        partnerships: {
            hero_eyebrow: 'الشراكات',
            hero_title: 'مبنية على ثلاثة عقود من الثقة والأداء والطموح المشترك',
            hero_sub: 'تمتد شبكة شراكات دلتا الدولية للطاقة لتشمل كبرى شركات النفط العالمية وشركات النفط الوطنية والمشغلين المستقلين والحكومات السيادية وشركات الهندسة والإنشاءات العالمية.',
            network_eyebrow: 'شبكة الشركاء',
            network_h2: 'بجانب أبرز شركات الطاقة في العالم',
            historical_partners: 'الشركاء التاريخيون',
            active_partners: 'شركاء المحفظة الحالية',
            details_eyebrow: 'تفاصيل الشراكات',
            details_h2: 'علاقات تُعرّف قطاعًا بأكمله',
            tab_historical: 'تاريخي',
            tab_active: 'محفظة EPC النشطة',
            summary_eyebrow: 'ملخص شبكة الشراكات',
            col_partner: 'الشريك',
            col_type: 'النوع',
            col_period: 'الفترة',
            col_context: 'السياق',
        },
        contact_page: {
            hero_eyebrow: 'تواصل معنا',
            hero_title: 'تواصل مع دلتا الدولية للطاقة',
            hero_sub: 'نرحب بالاستفسارات من الحكومات السيادية وشركات النفط الوطنية والمستثمرين المؤسسيين والمشغلين الدوليين والشركاء الاستراتيجيين الذين يشاركوننا التزامنا بفتح آفاق الطاقة الواعدة في العالم.',
            hq_eyebrow: 'المقر الرئيسي',
            hq_h2: 'مجموعة دلتا الدولية المحدودة',
            hq_loc: 'جدة، المملكة العربية السعودية',
            address_label: 'العنوان',
            phone_label: 'الهاتف',
            email_label: 'البريد الإلكتروني',
            website_label: 'الموقع الإلكتروني',
            inquiries_label: 'استفسارات المشاريع',
            form_eyebrow: 'إرسال استفسار',
            form_h2: 'نتطلع لسماع رأيكم.',
            closing_eyebrow: 'كلمة ختامية',
            closing_quote: '"تأسست دلتا الدولية للطاقة على إيمان بأن خبرة العالم العربي في الطاقة ورأس ماله وعلاقاته السيادية يمكن أن تجتمع مع المعايير الفنية والمالية الدولية لبناء شركة طاقة مستقلة بمستوى عالمي حقيقي. وبعد أربعة عقود — من بحر قزوين إلى وادي ألبرتاين، ومن دلتا النيل إلى الأحواض الغازية الأفغانية — أثبت هذا الإيمان جدواه. والفصل القادم هو الأكثر طموحًا حتى اليوم."',
        },
        footer: {
            tagline: 'استثمارات · تقنية · حلول',
            rights: 'جميع الحقوق محفوظة.',
            quick_links: 'روابط',
            contact_us: 'التواصل',
            address: '2827 شارع حسن نائب الحرم، حي الأندلس 8989، جدة 23326، المملكة العربية السعودية',
            col_explore: 'استكشف',
            col_portfolio: 'المحفظة',
            jeddah_sa: 'جدة، المملكة العربية السعودية',
            about_blurb: 'منصة مستقلة ومتكاملة للنفط والغاز. جزء من مجموعة دلتا الدولية المحدودة — جذورها في المملكة العربية السعودية، تعمل عبر أربع قارات.',
            headquarters: 'المقر الرئيسي',
        },
        contact: {
            full_name: 'الاسم الكامل',
            company: 'الشركة',
            country: 'الدولة',
            email: 'البريد الإلكتروني',
            enquiry_type: 'نوع الاستفسار',
            message: 'الرسالة',
            placeholder_enquiry: 'اختر نوع الاستفسار',
            success_title: 'تم إرسال الاستفسار',
            success_desc: 'شكرًا لكم. سيتواصل فريق دلتا الدولية للطاقة معكم قريبًا.',
            error_title: 'فشل الإرسال',
            error_desc: 'يرجى مراجعة البيانات والمحاولة مرة أخرى.',
            types: {
                investment: 'شراكة استثمارية',
                project: 'استفسار حول مشروع',
                media: 'إعلام',
                other: 'أخرى',
            },
        },
    },
};
